import { Button } from "@/components/ui/button";
import { NewWorkItemDialog, type WorkItemType } from "@/components/work-items/new-work-item-dialog";
import { useLoaderData } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/**
 * Four "New <type>" buttons (Feature / Bug / Task / Hotfix). Port of the
 * Next `_components/new-buttons.tsx`. Each button is a trigger for a
 * `<NewWorkItemDialog>` pre-set to its type — the dialog owns the
 * form + submission lifecycle.
 */

const TYPES: WorkItemType[] = ["feature", "bug", "standalone", "hotfix"];
// A hotfix skips the queue, so it's the team's call, never the client's.
const CLIENT_TYPES: WorkItemType[] = ["feature", "bug", "standalone"];
// Internal id stays `standalone` (the Hub-only WI flag + workflow
// variant) — the UI label is just "Task".
const LABEL_KEYS: Record<WorkItemType, string> = {
  feature: "feature",
  bug: "bug",
  standalone: "task",
  hotfix: "hotfix",
};

type Props = {
  /** When set, the dialog opens with this project preselected and the
   *  field locked — the caller already established context. */
  projectId?: string | undefined;
  size?: "sm" | "default";
};

export function NewWorkItemButtons({ projectId, size = "sm" }: Props) {
  const { t } = useTranslation();
  const { isCustomer } = useLoaderData({ from: "/_app" });
  return (
    <div className="flex items-center gap-1.5">
      {(isCustomer ? CLIENT_TYPES : TYPES).map((type) => (
        <NewWorkItemDialog
          key={type}
          type={type}
          asClient={isCustomer}
          {...(projectId ? { defaultProjectId: projectId, lockProject: true } : {})}
        >
          <Button size={size} variant="outline">
            {t("workItemList.newButton", { type: t(`workItemLabels.type.${LABEL_KEYS[type]}`) })}
          </Button>
        </NewWorkItemDialog>
      ))}
    </div>
  );
}
